import { createApi } from '@reduxjs/toolkit/query/react';

import { opsQuery } from 'store/query';

import {
  FindRun,
  FillRun,
  FillRuns,
  WebStatus,
  Suggestion,
  StatusKind,
  StatusItem,
  FindSearches,
  SearchEstimate,
  FindCandidates,
  NewSearchRequest,
  FindCandidatesRequest,
} from './types';

export * from './types';

// RESEARCH from the web (TSM plan §5.6, §7.1): fill runs, suggestions and
// the status line, on Ops under mailer/research (CloudFront sends mailer/*
// to Ops). Its own slice, like Accounting Overview. Approving goes to Main
// (research slice), which refreshes this one.
const BASE = 'mailer/research';
export const RESEARCH_WEB_TAG = 'ResearchWeb' as const;

// An older Ops build during a deploy has no route yet: the card says so
// instead of sending the page to Not found.
const headers = { 'allow-not-found': 'true' };

export const researchWebApi = createApi({
  baseQuery: opsQuery,
  tagTypes: [RESEARCH_WEB_TAG],
  reducerPath: 'researchWebApi',
  endpoints: (build) => ({
    queueFind: build.mutation<FindRun, void>({
      invalidatesTags: [RESEARCH_WEB_TAG],
      query: () => ({ method: 'post', url: `${BASE}/find/runs` }),
    }),
    researchWebStatus: build.query<WebStatus, void>({
      providesTags: [RESEARCH_WEB_TAG],
      query: () => ({ headers, method: 'get', url: `${BASE}/status` }),
    }),
    queueResearchFill: build.mutation<FillRun, number>({
      invalidatesTags: [RESEARCH_WEB_TAG],
      query: (id) => ({ method: 'post', url: `${BASE}/fill/shows/${id}` }),
    }),
    estimateSearch: build.mutation<SearchEstimate, NewSearchRequest>({
      query: (data) => ({
        data,
        method: 'post',
        url: `${BASE}/find/searches/estimate`,
      }),
    }),
    // New search and the Searches tab (owner 4 Nov).
    findSearches: build.query<FindSearches, void>({
      providesTags: [RESEARCH_WEB_TAG],
      query: () => ({
        headers,
        method: 'get',
        url: `${BASE}/find/searches?limit=20`,
      }),
    }),
    researchFillRuns: build.query<FillRuns, number>({
      providesTags: [RESEARCH_WEB_TAG],
      query: (id) => ({
        headers,
        method: 'get',
        url: `${BASE}/fill/shows/${id}/runs?limit=5`,
      }),
    }),
    cancelSearch: build.mutation<{ id: number; status: string }, number>({
      invalidatesTags: [RESEARCH_WEB_TAG],
      query: (id) => ({
        method: 'post',
        url: `${BASE}/find/searches/${id}/cancel`,
      }),
    }),
    queueSearch: build.mutation<
      SearchEstimate & { id: number; status: string },
      NewSearchRequest
    >({
      invalidatesTags: [RESEARCH_WEB_TAG],
      query: (data) => ({ data, method: 'post', url: `${BASE}/find/searches` }),
    }),
    researchSuggestions: build.query<{ suggestions: Suggestion[] }, number>({
      providesTags: [RESEARCH_WEB_TAG],
      query: (id) => ({
        headers,
        method: 'get',
        url: `${BASE}/fill/shows/${id}/suggestions`,
      }),
    }),
    dismissResearchFillRun: build.mutation<
      { id: number; dismissedAt: string },
      number
    >({
      invalidatesTags: [RESEARCH_WEB_TAG],
      query: (id) => ({
        method: 'post',
        url: `${BASE}/fill/runs/${id}/dismiss`,
      }),
    }),
    findRuns: build.query<
      { runs: FindRun[]; switchedOn: boolean; offReason: string | null },
      void
    >({
      providesTags: [RESEARCH_WEB_TAG],
      query: () => ({
        headers,
        method: 'get',
        url: `${BASE}/find/runs?limit=5`,
      }),
    }),
    decideCandidate: build.mutation<
      { id: number; status: string },
      { id: number; action: 'dismiss' | 'restore' }
    >({
      invalidatesTags: [RESEARCH_WEB_TAG],
      query: ({ id, action }) => ({
        method: 'post',
        url: `${BASE}/find/candidates/${id}/${action}`,
      }),
    }),
    researchFileLink: build.query<
      { url: string },
      { id: number; kind: 'floorPlan' | 'prospectus' }
    >({
      // A fresh 5-minute link every time (never cached).
      keepUnusedDataFor: 0,
      query: ({ id, kind }) => ({
        method: 'get',
        url: `${BASE}/fill/shows/${id}/files/${kind}`,
      }),
    }),
    researchWebStatusItems: build.query<
      { total: number; rows: StatusItem[] },
      { page?: number; kind: StatusKind }
    >({
      providesTags: [RESEARCH_WEB_TAG],
      query: ({ kind, page = 1 }) => ({
        headers,
        method: 'get',
        url: `${BASE}/status/items?kind=${kind}&page=${page}&pageSize=50`,
      }),
    }),
    findCandidates: build.query<FindCandidates, FindCandidatesRequest>({
      providesTags: [RESEARCH_WEB_TAG],
      query: (params) => ({
        headers,
        method: 'get',
        url: `${BASE}/find/candidates?${new URLSearchParams(
          Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== '')
            .map(([key, value]) => [key, String(value)])
        ).toString()}`,
      }),
    }),
  }),
});

export const {
  useFindRunsQuery,
  useFindSearchesQuery,
  useQueueFindMutation,
  useQueueSearchMutation,
  useFindCandidatesQuery,
  useCancelSearchMutation,
  useResearchFillRunsQuery,
  useEstimateSearchMutation,
  useResearchWebStatusQuery,
  useDecideCandidateMutation,
  useResearchSuggestionsQuery,
  useLazyResearchFileLinkQuery,
  useQueueResearchFillMutation,
  useResearchWebStatusItemsQuery,
  useDismissResearchFillRunMutation,
} = researchWebApi;
